import * as React from 'react';
import useSummaryCalculator from "../helpers/useSummaryCalculator";
import { formatDisplayDate, formatPriceWithCurrency, getOfferIntervalName, getOfferPrice, isSubscription } from "../index/utils";
import ProductDescription from "../components/productDescription";
import { resolvePlanName } from "../index/planNameOverrides";
import { applyDiscount, discountLines, reconciledSaving, type Discount } from "../components/discount";

type Props = Readonly<{
	offer: {}
	startDate: Date
	// The exact amount the customer will be charged, in the major unit (payable.amount
	// / 100). When known — the payment step — it is authoritative and shown as-is; the
	// pro-rata first-month line is then derived from it so the breakdown reconciles.
	// Absent before then (the direct debit step), so the total is a pro-rata estimate.
	amountDueToday?: number
	// The automatic discount on this sale (if any). The summary computes the discounted
	// price from its percentages so the saving shows from the first step, and prefers the
	// authoritative charged amount once that comes back reduced at the payment step.
	discount?: Discount | null
}>

function SummaryExtended({ offer, startDate, amountDueToday, discount }: Props) {
	const summary = useSummaryCalculator(startDate, offer);
	const currency = offer?.priceCurrency;

	// A one-off product has no billing interval and nothing due next month, so the
	// recurring half of the summary does not apply — but it can still carry a joining
	// fee, which is why the fee line is not gated on this.
	const subscription = isSubscription(offer);

	const authoritative = typeof amountDueToday === 'number';
	const undiscounted = summary.amountTotal;

	// The discounted amount due today: from the authoritative charged amount when it is
	// known and already reduced (the real discount on the sale), else computed from the
	// discount's own percentages so the saving is visible before the sale is priced.
	const { discountedTotal, hasDiscount } = applyDiscount(summary, discount);
	const discountedNow = (authoritative && amountDueToday < undiscounted) ? amountDueToday : discountedTotal;
	// Reconciled to the shown totals so struck − discount lines == amount due exactly.
	const saving = hasDiscount ? Math.max(0, reconciledSaving(undiscounted, discountedNow)) : 0;
	const hasSaving = saving > 0.005;
	const lines = discountLines(summary, discount, startDate, saving);

	const total = hasSaving ? discountedNow : (authoritative ? amountDueToday : undiscounted);

	// The membership portion due today (before the joining fee). When a saving is shown
	// the breakdown is the undiscounted detail (reconciled by the Discount line below);
	// otherwise it is derived from the authoritative total so first month + fee == total.
	const firstMonth = hasSaving
		? summary.membershipDueToday
		: (authoritative ? amountDueToday - summary.adminFee : summary.membershipDueToday);

	return (<>
		<div className="p-6 sm:p-8">
			<h3>ORDER SUMMARY</h3>
			<h5 className="mb-8 uppercase">{resolvePlanName(offer?.showcaseIri, offer?.name)}</h5>

			<div className="text-right">
				{formatPriceWithCurrency(getOfferPrice(offer), currency)}{subscription && <> / {getOfferIntervalName(offer)}</>}
			</div>

			{/*
				Was a hardcoded list of off-peak times, classes and cancellation terms
				rendered for every product — the same block that was removed from the
				membership step. The joining fee it also carried is already shown below.
			*/}
			<ProductDescription description={offer?.description}/>

			<ul>
				<li>Starts on {formatDisplayDate(startDate)}</li>
				{subscription && <li>1st Direct Debit: {formatDisplayDate(summary.directDebitFirstDay)}</li>}
			</ul>

			{(subscription || summary.adminFee > 0) && <>
				<hr className="my-8"/>

				{subscription &&
					<div className="flex flex-wrap gap-x-8 justify-between my-4">
						<div>{summary.firstMonthPrepaid ? 'First payment:' : 'First month:'}</div>
						<div>{formatPriceWithCurrency(firstMonth, currency)}</div>
					</div>
				}

				{subscription && summary.firstMonthPrepaid &&
					<p className="text-sm -mt-2">Covers the rest of this month plus next month, paid in advance.</p>
				}

				{summary.adminFee > 0 &&
					<div className="flex flex-wrap gap-x-8 justify-between mt-4">
						<div>Joining fee:</div>
						<div>{formatPriceWithCurrency(summary.adminFee, currency)}</div>
					</div>
				}

				{hasSaving && lines.length > 0 &&
					<div className="mt-4 text-primary-700 font-bold">
						<div>Discount</div>
						{lines.map((line, i) =>
							<div key={i} className="flex flex-wrap gap-x-8 justify-between font-normal text-sm mt-1">
								<div>{line.label}</div>
								<div>&minus;{formatPriceWithCurrency(line.amount, currency)}</div>
							</div>
						)}
					</div>
				}
			</>}
		</div>

		<div className="p-8 bg-primary-200 font-bold text-xl">
			<div className="flex flex-wrap gap-x-8 justify-between">
				<div>{authoritative ? 'Amount due today:' : 'Estimated total:'}</div>
				<div>
					{hasSaving &&
						<span className="line-through font-normal text-base opacity-60 mr-2">{formatPriceWithCurrency(undiscounted, currency)}</span>
					}
					{formatPriceWithCurrency(total, currency)}
				</div>
			</div>
			{hasSaving &&
				<div className="text-sm font-normal mt-1">You save {formatPriceWithCurrency(saving, currency)}</div>
			}
		</div>

		{subscription && <div className="p-8 bg-grey-100">
			<div className="flex flex-wrap gap-x-8 justify-between font-bold text-xl mb-2">
				<div>Total due from next month:</div>
				<div>{formatPriceWithCurrency(summary.monthlyFee, currency)}</div>
			</div>
			<div>Your next billing date is {formatDisplayDate(summary.directDebitFirstDay)}</div>
		</div>}
	</>);
}

export default SummaryExtended;
